import { create } from "zustand";
import { Edge } from "@xyflow/react";
import { isEntityBatchNodeType, NodeData } from "../types";
import { createNodeTypes, edgeTypes } from "../nodes/nodeTypes";
import { nanoid } from "nanoid";
import { CustomNode, TriggerType, WorkflowBuilderState } from "./useWorkflowBuilderStore.d";
import { type ScheduleConfig } from "@quake/isomorphic/schemas/automation-nodes.schema";
import { getContextualActionParameters } from "./action-defaults";

const USER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
/**
 * Generate a human-readable label from schedule config
 */
export function generateScheduleLabel(schedule: ScheduleConfig): string {
  const tz = schedule.timezone ? schedule.timezone : "";
  const rateValue = schedule.rateValue ?? 1;
  const rateUnit = schedule.rateUnit ?? "hour";
  const windowConfig = schedule.windowConfig ?? {};
  const time = schedule.time;

  const parts: string[] = [];

  // Frequency part
  const unitLabel = rateValue === 1 ? rateUnit : `${rateUnit}s`;
  parts.push(`Every ${rateValue} ${unitLabel}`);

  // Time part (for day/week/month frequencies)
  if (rateUnit === "day" || rateUnit === "week" || rateUnit === "month") {
    if (time) {
      const hour = Number.isFinite(time.hour) ? time.hour : 0;
      const minute = Number.isFinite(time.minute) ? time.minute : 0;
      parts.push(`at ${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`);
    }
  }

  // Time window part
  if (windowConfig.hours?.length && windowConfig.hours.length < 24) {
    const start = windowConfig.hours[0];
    const end = windowConfig.hours[windowConfig.hours.length - 1];
    const consecutive = windowConfig.hours.every(
      (hour, index) => index === 0 || hour === (windowConfig.hours?.[index - 1] ?? -1) + 1,
    );
    parts.push(
      consecutive
        ? `between ${start}:00-${end}:59`
        : `during hours ${windowConfig.hours.map((hour) => `${hour}:00`).join(", ")}`,
    );
  }

  // Day filter part
  if (windowConfig.daysOfWeek?.length) {
    const days = windowConfig.daysOfWeek;
    const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const fullDayLabels = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

    const isWeekdays = days.length === 5 && [1, 2, 3, 4, 5].every((d) => days.includes(d));
    const isWeekends = days.length === 2 && [0, 6].every((d) => days.includes(d));
    const isEveryDay = days.length === 7;

    let dayStr: string;
    if (isEveryDay) {
      dayStr = "every day";
    } else if (isWeekdays) {
      dayStr = "on weekdays";
    } else if (isWeekends) {
      dayStr = "on weekends";
    } else if (days.length === 1 && days[0] != null) {
      dayStr = `on ${fullDayLabels[days[0]]}`;
    } else {
      dayStr = `on ${days.map((d: number) => dayLabels[d]).join(", ")}`;
    }

    // Week of month modifier
    if (windowConfig.weekOfMonth && windowConfig.weekOfMonth > 0) {
      const weekLabels = ["", "first", "second", "third", "fourth", "last"];
      const weekLabel = weekLabels[windowConfig.weekOfMonth] || "";
      if (days.length === 1 && days[0] != null) {
        dayStr = `on the ${weekLabel} ${fullDayLabels[days[0]]}`;
      } else {
        dayStr = `on the ${weekLabel} week, ${dayStr}`;
      }
    }

    parts.push(dayStr);
  }

  return parts.join(" ") + (USER_TZ == tz ? "" : ` (${tz})`);
}

// Small helper: map trigger types to human-readable labels
const TRIGGER_LABELS: Record<TriggerType, string> = {
  manual: "Manual Trigger",
  webhook: "Webhook Trigger",
  interval: "Interval Trigger",
  event: "Event Trigger",
  email: "Email Trigger",
  app: "Polling Trigger",
};

const isContainerNodeType = (type?: string | null) => type === "iterator" || isEntityBatchNodeType(type);

export const workflowBuilderStoreFn = () =>
  create<WorkflowBuilderState>((set, get) => {
    return {
      reset: () =>
        set({
          nodes: [],
          edges: [],
          activeSheet: { type: null },
          $rfInstance: null,
          meta: { direction: "left-to-right" },
          isDirty: false,
          readOnly: false,
          allowActionPreview: true,
        }),
      readOnly: false,
      allowActionPreview: true,
      meta: {
        direction: "left-to-right",
      },
      setMeta: (meta: any) => set({ meta }),
      setReadOnly: (readOnly) => set({ readOnly }),
      setAllowActionPreview(allow) {
        set({ allowActionPreview: allow });
      },
      isDirty: false,
      setIsDirty: (isDirty) => set({ isDirty }),
      // Initial States
      nodes: [],
      edges: [],
      activeSheet: { type: null },
      $rfInstance: null,
      setInstance: (instance) => set({ $rfInstance: instance }),

      // Node and Edge Types
      nodeTypes: createNodeTypes((id) => ({
        onAddAction: ({ edgeColor }) => get().handleAddAction(id, { edgeColor }),
        onDelete: () => get().handleDeleteNode(id),
        onEdit: () => get().handleEditNode(id),
        readOnly: get().readOnly,
        allowActionPreview: get().allowActionPreview,
        onAddChildAction: () => get().handleAddChildAction(id),
      })),
      edgeTypes,

      // Sheet Management
      openSheet: (type, context) => set({ activeSheet: { type, context } }),
      closeSheet: () => set({ activeSheet: { type: null } }),

      // Setters
      setNodes: (nodes) =>
        set((state) => ({ isDirty: true, nodes: typeof nodes === "function" ? nodes(state.nodes) : nodes })),
      setEdges: (edges) =>
        set((state) => ({ isDirty: true, edges: typeof edges === "function" ? edges(state.edges) : edges })),

      // Efficiently update a single node by id
      updateNode: (nodeId, updater) => {
        const { $rfInstance } = get();
        if (!$rfInstance) {
          throw new Error("ReactFlow instance not found");
        }

        $rfInstance.updateNode(nodeId, updater);
        return set((state) => {
          const foundNode = state.nodes.find((node) => node.id === nodeId);
          if (foundNode) {
            console.log("🔧 updateNode", nodeId, state.nodes, updater(foundNode));
          }
          return {
            isDirty: true,
            nodes: state.nodes.map((node) => (node.id === nodeId ? updater(node) : node)),
          };
        });
      },

      // Update iterator container size based on child nodes
      updateIteratorSize: (iteratorId) => {
        const { $rfInstance, nodes } = get();
        const currentNodes = $rfInstance?.getNodes?.() ?? nodes;
        const iteratorNode = currentNodes.find((n) => n.id === iteratorId);
        if (!iteratorNode || !isContainerNodeType(iteratorNode.type)) return;

        const childNodes = currentNodes.filter((n) => n.parentId === iteratorId);
        if (childNodes.length === 0) {
          // No children, use minimum size
          get().updateNode(iteratorId, (node) => ({
            ...node,
            style: { ...node.style, width: 400, height: 300 },
            data: { ...node.data, childNodes: [] },
          }));
          return;
        }

        // Calculate bounding box of all children
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;

        const PADDING = 50;
        const DEFAULT_NODE_WIDTH = 200;
        const DEFAULT_NODE_HEIGHT = 80;

        childNodes.forEach((child) => {
          const x = child.position?.x || 0;
          const y = child.position?.y || 0;
          const width =
            typeof child.width === "number"
              ? child.width
              : typeof child.style?.width === "number"
                ? child.style.width
                : DEFAULT_NODE_WIDTH;
          const height =
            typeof child.height === "number"
              ? child.height
              : typeof child.style?.height === "number"
                ? child.style.height
                : DEFAULT_NODE_HEIGHT;

          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x + width);
          maxY = Math.max(maxY, y + height);
        });

        // Add padding
        const containerWidth = Math.max(400, maxX - minX + PADDING * 2);
        const containerHeight = Math.max(300, maxY - minY + PADDING * 2);

        // Update iterator node dimensions
        get().updateNode(iteratorId, (node) => ({
          ...node,
          style: { ...node.style, width: containerWidth, height: containerHeight },
          data: { ...node.data, childNodes: childNodes.map((c) => c.id) },
        }));
      },

      // Handlers
      handleEdgeClick: (nodeId) => {
        get().openSheet("action", { nodeId });
      },

      handleDeleteNode: (nodeId) => {
        const { nodes, edges, $rfInstance } = get();
        if (!$rfInstance) {
          throw new Error("ReactFlow instance not found");
        }

        const nodeToDelete = nodes.find((n) => n.id === nodeId);
        const connectedEdges = edges.filter((edge) => edge.source === nodeId);
        const childNodeIds = connectedEdges.map((edge) => ({ id: edge.target }));

        // If deleting an iterator, also delete its child nodes (nodes with parentId === nodeId)
        const iteratorChildren = isContainerNodeType(nodeToDelete?.type)
          ? nodes.filter((n) => n.parentId === nodeId).map((n) => ({ id: n.id }))
          : [];

        $rfInstance.deleteElements({
          nodes: [{ id: nodeId }, ...childNodeIds, ...iteratorChildren],
        });
        set({ isDirty: true });
      },

      handleEditNode: (nodeId) => {
        const { nodes } = get();
        const node = nodes.find((n) => n.id === nodeId);
        if (!node) return;

        if (node.type === "trigger") {
          // Handle different trigger types
          switch (node.data.triggerType) {
            case "interval":
              get().openSheet("interval", {
                nodeId,
                triggerType: "interval",
                schedule: node.data.schedule,
              });
              break;
            case "email":
              get().openSheet("email", {
                nodeId,
                triggerType: "email",
                allowedEmails: node.data.allowedEmails,
                triggerEmail: node.data.triggerEmail,
              });
              break;
            case "event":
              get().openSheet("platformEvents", {
                nodeId,
                triggerType: "event",
                platformEvent: node.data.event
                  ? {
                      ...node.data.event,
                      runMode: node.data.event.runMode || "automatic",
                    }
                  : undefined,
              });
              break;
            case "manual":
              get().openSheet("manual", {
                nodeId,
                triggerType: "manual",
                recordTypes: node.data.recordTypes || [],
              });
              break;
            case "webhook":
              get().openSheet("webhook", {
                nodeId,
                triggerType: node.data.triggerType,
              });
              break;
            case "app":
              get().openSheet("app", {
                nodeId,
                triggerType: "app",
                triggerId: node.data.triggerId,
                triggerAppId: node.data.triggerAppId,
                note: node.data.note,
                triggerInputs: node.data.triggerInputs,
              });
              break;
            default:
              // Fallback to trigger sheet for unknown types
              get().openSheet("trigger", {
                nodeId,
                triggerType: node.data.triggerType,
              });
              break;
          }
        } else {
          get().openSheet("editAction", { nodeId });
        }
      },

      handleAddAction: (nodeId, options) => {
        get().openSheet("action", { nodeId, ...options });
      },

      handleAddChildAction: (iteratorId) => {
        // Open action sheet with flag to add node inside iterator
        get().openSheet("action", { nodeId: iteratorId, addInsideIterator: true });
      },

      handleTriggerSelect: (type, appTriggerId, triggerAppId) => {
        const { nodes, activeSheet, addNode, generateTriggerNodeId, updateNode, openSheet, closeSheet } = get();

        const label = TRIGGER_LABELS[type] ?? "Trigger";
        const existingNodeId = activeSheet.context?.nodeId;

        // Utility – create webhook data bundle
        const makeWebhookData = () => {
          const webhookToken = nanoid();
          return {
            webhookToken,
            webhookUrl: `${location.origin}/api/external/webhook/trigger/${webhookToken}`,
            output_schema: {
              type: "object",
              properties: {
                method: { type: "string" },
                headers: { type: "object" },
                body: { type: "object" },
                query: { type: "object" },
                ip: { type: "string" },
                userAgent: { type: "string" },
              },
            },
          } as const;
        };

        // Utility – open the configuration sheet for the selected trigger type
        const openConfigSheet = (nodeId: string, extra: Record<string, any> = {}) => {
          switch (type) {
            case "interval":
              openSheet("interval", { nodeId, triggerType: type, ...extra });
              break;
            case "email":
              openSheet("email", { nodeId, triggerType: type, ...extra });
              break;
            case "event":
              openSheet("platformEvents", { nodeId, triggerType: type, ...extra });
              break;
            case "manual":
              openSheet("manual", { nodeId, triggerType: type, ...extra });
              break;
            case "webhook":
              openSheet("webhook", { nodeId, triggerType: type, ...extra });
              break;
            case "app":
              openSheet("app", { nodeId, triggerType: type, triggerId: appTriggerId, triggerAppId, ...extra });
              break;
            default:
              closeSheet();
          }
        };

        // ────────────────────────────────────
        // 1. Editing an existing trigger node
        // ────────────────────────────────────
        if (existingNodeId) {
          const newReadableId = generateTriggerNodeId(type, null, nodes);
          let webhookData = {};

          updateNode(existingNodeId, (node) => {
            if (type === "webhook") {
              webhookData = !node.data.webhookToken
                ? makeWebhookData()
                : {
                    webhookToken: node.data.webhookToken,
                    webhookUrl: `${location.origin}/api/external/webhook/trigger/${node.data.webhookToken}`,
                  };
            }
            return {
              ...node,
              data: {
                ...node.data,
                readableId: newReadableId,
                triggerType: type,
                label,
                // Keep or reset type-specific fields
                schedule: type === "interval" || type === "app" ? node.data.schedule : undefined,
                allowedEmails: type === "email" ? node.data.allowedEmails : undefined,
                triggerEmail: type === "email" ? node.data.triggerEmail : undefined,
                event: type === "event" ? node.data.event : undefined,
                source: type === "event" ? node.data.source : undefined,
                triggerId: type === "app" ? appTriggerId : undefined,
                triggerAppId: type === "app" ? triggerAppId : undefined,
                ...webhookData,
              },
            };
          });

          openConfigSheet(existingNodeId, webhookData);
          return; // Done editing
        }

        // ────────────────────────────────────
        // 2. Creating a brand-new trigger node
        // ────────────────────────────────────
        const initialPosition = nodes.length === 0 ? { x: 100, y: 100 } : { x: 100, y: 100 + nodes.length * 200 };

        const readableId = generateTriggerNodeId(type, null, nodes);
        let webhookData = {};
        if (type === "webhook") {
          webhookData = makeWebhookData();
        }

        const newNode = addNode({
          type: "trigger",
          position: initialPosition,
          data: {
            triggerType: type,
            readableId,
            label,
            triggerId: appTriggerId,
            triggerAppId,
            ...webhookData,
          },
        });

        if (type === "webhook") {
          openConfigSheet(newNode.id, webhookData);
        } else {
          openConfigSheet(newNode.id);
        }
      },

      // Helper function to generate readable, unique IDs for action nodes
      generateActionNodeId: (actionName: string, existingNodes: CustomNode[]) => {
        // Convert action name to camelCase and make it variable-friendly
        let baseId = actionName
          .toLowerCase()
          .replace(/[^a-zA-Z0-9\s]/g, "") // Remove special characters including dots
          .replace(/\s+/g, " ") // Normalize spaces
          .trim()
          .split(" ")
          .map((word, index) => (index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
          .join("")
          .replace(/\./g, "_"); // Replace any remaining dots with underscores

        // Handle edge case where baseId might be empty after cleaning
        if (!baseId) {
          baseId = "action";
        }

        // Check if this ID already exists
        const existingIds = existingNodes.map((node) => node.data.readableId);
        let uniqueId = baseId;
        let counter = 1;

        while (existingIds.includes(uniqueId)) {
          uniqueId = `${baseId}${counter}`;
          counter++;
        }

        return uniqueId;
      },

      handleActionSelect: (actionId, options, app) => {
        const { activeSheet, nodes, addNode, closeSheet, updateNode, $rfInstance } = get();
        if (!$rfInstance) {
          throw new Error("ReactFlow instance not found");
        }

        const context = activeSheet.context;
        const finalApp = app || context?.app;
        if (!context?.nodeId || !finalApp) return;

        const sourceNode = nodes.find((node) => node.id === context.nodeId);
        if (!sourceNode) return;

        const action = finalApp.actions.find((a) => a.id === actionId);
        if (!action) return;

        // The addNode function will handle positioning and generate IDs
        // Pass the addInsideIterator flag if it's set in context
        const newNode = addNode({
          type: "action",
          position: { x: 0, y: 0 },
          sourceNodeId: context.nodeId,
          forceInsideIterator: context.addInsideIterator,
          ...options,
        });

        // Generate a more specific readable ID for the action node
        const newReadableId = get().generateActionNodeId(action.name, nodes);

        // Update the authoritative store node so edit sheets and React Flow stay in sync.
        const { id, name, logo_url, origin_id, installed_id } = finalApp;
        const parentNode = nodes.find((node) => node.id === newNode.parentId);
        const contextualParameters = getContextualActionParameters({ actionId: action.id, parentNode });

        updateNode(newNode.id, (node) => ({
          ...node,
          data: {
            ...node.data,
            readableId: newReadableId,
            label: action.name,
            app: { id, name, logo_url, origin_id, installed_id: installed_id ?? id },
            action: {
              ...action,
              logo_url: action.logo_url ?? undefined,
              parameters: contextualParameters ?? action.parameters,
            },
            output_schema: action.output_schema,
          },
        }));

        closeSheet();
      },

      handleIntervalSubmit: (schedule) => {
        const { activeSheet, nodes, setNodes, closeSheet } = get();
        if (!activeSheet.context?.nodeId) return;

        // Generate a new readable ID based on the interval configuration
        const newReadableId = get().generateTriggerNodeId(
          "interval",
          {
            schedule,
          },
          nodes,
        );

        setNodes(
          nodes.map((node) => {
            if (node.id === activeSheet.context?.nodeId) {
              return {
                ...node,
                // Keep the same stable node ID - only update readableId
                data: {
                  ...node.data,
                  readableId: newReadableId,
                  triggerType: "interval",
                  schedule,
                  label: generateScheduleLabel(schedule),
                },
              };
            }
            return node;
          }),
        );

        // No need to update edges since node ID remains stable
        closeSheet();
      },
      handleEmailSubmit: (values) => {
        const { activeSheet, nodes, setNodes, closeSheet } = get();
        if (!activeSheet.context?.nodeId) return;

        setNodes(
          nodes.map((node) => {
            if (node.id === activeSheet.context?.nodeId) {
              return {
                ...node,
                // Preserve readableId so saving settings does not break expressions referencing this trigger.
                data: {
                  ...node.data,
                  triggerType: "email",
                  allowedEmails: values.allowedEmails,
                  triggerEmail: values.triggerEmail,
                  label: `Each time ${values.triggerEmail} receives an email this will trigger the workflow`,
                  output_schema: {
                    type: "object",
                    properties: {
                      from: { type: "string" },
                      to: { type: "string" },
                      body: { type: "string" },
                      body_html: { type: "string" },
                      cc: {
                        type: "array",
                        items: {
                          type: "string",
                        },
                      },
                      attachments: {
                        type: "array",
                        items: {
                          type: "object",
                          properties: {
                            contentType: { type: "string" },
                            filename: { type: "string" },
                            size: { type: "string" },
                            url: { type: "string" },
                          },
                        },
                      },
                    },
                  },
                },
              };
            }
            return node;
          }),
        );

        // No need to update edges since node ID remains stable
        closeSheet();
      },

      handleAppSubmit: (schedule) => {
        const { activeSheet, nodes, setNodes, closeSheet } = get();
        if (!activeSheet.context?.nodeId) return;

        // Generate a new readable ID based on the app configuration
        const newReadableId = get().generateTriggerNodeId(
          "app",
          {
            schedule,
          },
          nodes,
        );

        setNodes(
          nodes.map((node) => {
            if (node.id === activeSheet.context?.nodeId) {
              return {
                ...node,
                // Keep the same stable node ID - only update readableId
                data: {
                  ...node.data,
                  readableId: newReadableId,
                  triggerType: "app",
                  schedule,
                  label: generateScheduleLabel(schedule),
                },
              };
            }
            return node;
          }),
        );

        // No need to update edges since node ID remains stable

        closeSheet();
      },

      handlePlatformEventSubmit: (data) => {
        const { activeSheet, nodes, setNodes, closeSheet, $rfInstance, applyLayout } = get();
        if (!$rfInstance) return;
        const context = activeSheet.context;
        if (!context || !context.nodeId) return;
        const node = nodes.find((n) => n.id === context.nodeId);

        // Generate a new readable ID based on the event configuration
        const newReadableId = get().generateTriggerNodeId(
          "event",
          {
            event: {
              record: data.record,
              events: data.events,
              runMode: data.runMode,
            },
          },
          nodes,
        );
        const getLabel = () => {
          const formatter = new Intl.ListFormat("en", { style: "long", type: "disjunction" });
          const events = data.events.flatMap((e) =>
            e.split(".").filter((e) => e !== "recruitment" && e !== data.record),
          );
          return formatter.format(events);
        };
        setNodes(
          nodes.map((node) => {
            if (node.id === context.nodeId) {
              return {
                ...node,
                // Keep the same stable node ID - only update readableId
                data: {
                  output_schema: data.schema,
                  ...node.data,
                  readableId: newReadableId,
                  triggerType: "event",
                  source: "platform",
                  event: {
                    record: data.record,
                    recordId: data.recordId!,
                    events: data.events,
                    runMode: data.runMode,
                  },
                  recordId: data.recordId,
                  label: `${data.record.charAt(0).toUpperCase() + data.record.slice(1)} ${getLabel()}`,
                },
              };
            }
            return node;
          }),
        );

        // Also update the node in the ReactFlow instance for immediate UI update
        if ($rfInstance && context.nodeId) {
          $rfInstance.updateNode(context.nodeId, {
            data: {
              ...node?.data,
              output_schema: data.schema,
              type: node?.data.type ?? "trigger",
              readableId: newReadableId,
              triggerType: "event",
              source: "platform",
              event: data,
              recordId: data.recordId,
              label: `${data.record.charAt(0).toUpperCase() + data.record.slice(1)} ${getLabel()}`,
            },
          });
        }

        // No need to update edges since node ID remains stable
        requestAnimationFrame(() => {
          applyLayout("LR");
        });
        closeSheet();
      },

      handleWebhookSubmit: () => {
        const { activeSheet, closeSheet } = get();
        if (!activeSheet.context?.nodeId) return;

        // Webhook configuration is already saved when the sheet opens
        // Just close the sheet since the webhook token and URL are already set
        closeSheet();
      },

      handleAppTriggerSubmit: (data) => {
        const { activeSheet, nodes, setNodes, closeSheet } = get();
        if (!activeSheet.context?.nodeId) return;

        // Generate a new readable ID based on the app trigger
        // We can reuse the existing ID or generate one. "readableId" is passed in data.
        const newReadableId = get().generateTriggerNodeId(
          "app",
          {
            triggerId: data.triggerId,
          },
          nodes,
        );

        setNodes(
          nodes.map((node) => {
            if (node.id === activeSheet.context?.nodeId) {
              return {
                ...node,
                data: {
                  ...node.data,
                  readableId: newReadableId,
                  triggerType: "app",
                  triggerId: data.triggerId,
                  triggerAppId: data.triggerAppId,
                  label: data.label,
                  note: data.note,
                  schedule: data.schedule,
                  triggerInputs: data.triggerInputs,
                },
              };
            }
            return node;
          }),
        );
        closeSheet();
      },

      handleManualSubmit: (records, schema) => {
        const { activeSheet, updateNode } = get();
        if (!activeSheet.context?.nodeId) return;
        updateNode(activeSheet.context.nodeId, (node) => ({
          ...node,
          data: {
            ...node.data,
            triggerType: "manual",
            recordTypes: records,
            input_schema: schema,
            label: "Manual Trigger",
          },
        }));
      },

      onConnect: (params) => {
        const { setEdges, applyLayout } = get();
        if (params.source && params.target) {
          setEdges((eds) => [
            ...eds,
            {
              id: `${params.source}-${params.target}`.replace(/\./g, "_"),
              source: params.source,
              target: params.target,
              // XYFlow adds handle ids under sourceHandle
              sourceHandle: (params as any).sourceHandle,
              type: "animated",
              animated: true,
            },
          ]);
          requestAnimationFrame(() => {
            applyLayout("LR");
          });
        }
      },

      onConnectEnd: (event, connectionState, screenToFlowPosition) => {
        if (!event || !connectionState.from) return;
        const targetIsPane = (event.target as Element).classList.contains("react-flow__pane");
        if (targetIsPane) {
          // connectionState.from is an XYPosition, we need to find the node at that position
          const { $rfInstance } = get();
          if ($rfInstance) {
            const nodes = $rfInstance.getNodes();
            const position = connectionState.from;
            const nodeAtPosition = nodes.find(
              (node) =>
                position.x >= node.position.x &&
                position.x <= node.position.x + 250 && // Assuming node width
                position.y >= node.position.y &&
                position.y <= node.position.y + 80,
            );
            if (nodeAtPosition) {
              get().openSheet("action", { nodeId: nodeAtPosition.id });
            }
          }
        }
        if (connectionState.isValid) {
          requestAnimationFrame(() => {
            get().applyLayout("LR");
          });
        }
      },

      onReconnect: (oldEdge, newConnection) => {
        const { setEdges } = get();
        setEdges((eds) => eds.map((edge) => (edge.id === oldEdge.id ? { ...edge, ...newConnection } : edge)));
      },

      onReconnectEnd: (_, oldEdge, handleType) => {
        //
      },

      onNodesDelete: (deletedNodes) => {
        const { nodes, edges, setNodes, setEdges, updateIteratorSize } = get();
        if (!deletedNodes || deletedNodes.length === 0) return;

        const deletedIds = new Set(deletedNodes.map((n) => n.id));

        // Compute which iterators are affected (parents of deleted nodes)
        const affectedIteratorIds = new Set(
          deletedNodes.map((n) => n.parentId).filter((id): id is string => typeof id === "string" && id.length > 0),
        );

        // Remove deleted nodes from store nodes
        const remainingNodes = nodes.filter((n) => !deletedIds.has(n.id));

        // For each affected iterator, refresh its childNodes array to reflect remaining children
        const updatedNodes = remainingNodes.map((n) => {
          if (isContainerNodeType(n.type) && affectedIteratorIds.has(n.id)) {
            const childIds = remainingNodes.filter((c) => c.parentId === n.id).map((c) => c.id);
            return {
              ...n,
              data: {
                ...n.data,
                childNodes: childIds,
              },
            };
          }
          return n;
        });

        // Remove edges connected to deleted nodes
        const remainingEdges = edges.filter((e) => !deletedIds.has(e.source) && !deletedIds.has(e.target));

        setNodes(updatedNodes);
        setEdges(remainingEdges);

        // Recalculate size for each affected iterator
        affectedIteratorIds.forEach((id) => updateIteratorSize(id));
      },

      onEdgesDelete: (deletedEdges) => {
        const { setEdges } = get();
        setEdges((eds) => eds.filter((edge) => !deletedEdges.some((deleted) => deleted.id === edge.id)));
      },

      addNode: (instruction) => {
        const { nodes, $rfInstance, applyLayout, meta } = get();
        if (!$rfInstance) {
          throw new Error("ReactFlow instance not found");
        }
        const { position, sourceNodeId, type, data, edgeColor, forceInsideIterator } = instruction;

        // Get the layout direction from meta
        const direction: "left-to-right" | "top-to-bottom" = meta?.direction || "left-to-right"; // Default to left-to-right

        // Calculate better position to avoid overlaps based on direction
        let newPosition = { ...position };
        let parentNodeId: string | undefined = undefined;

        if (sourceNodeId) {
          // Position relative to source node
          const sourceNode = nodes.find((n) => n.id === sourceNodeId);
          if (sourceNode) {
            // Check if we should add inside an iterator:
            // 1. If forceInsideIterator flag is set and source is iterator
            // 2. If source node is inside an iterator (has parentId), continue adding inside
            const shouldAddInsideIterator = forceInsideIterator && isContainerNodeType(sourceNode.type);
            const sourceIsInsideIterator = sourceNode.parentId;

            if (shouldAddInsideIterator) {
              // Force adding inside the iterator
              parentNodeId = sourceNode.id;
              const children = nodes.filter((n) => n.parentId === sourceNodeId);
              if (children.length > 0) {
                const lastChild = children[children.length - 1];
                if (lastChild && direction === "top-to-bottom") {
                  newPosition = {
                    x: lastChild.position.x,
                    y: lastChild.position.y + 150,
                  };
                } else if (lastChild) {
                  newPosition = {
                    x: lastChild.position.x + 250,
                    y: lastChild.position.y,
                  };
                }
              } else {
                if (instruction.sourceNodeId) {
                  const sourceNode = $rfInstance.getNode(instruction.sourceNodeId)?.measured;
                  if (sourceNode && sourceNode.width && sourceNode.height) {
                    newPosition = {
                      x: sourceNode.width / 2,
                      y: sourceNode.height / 2,
                    };
                  } else {
                    newPosition = { x: 100, y: 120 };
                  }
                }
              }
            } else if (sourceIsInsideIterator) {
              // Source is inside an iterator, so add the new node inside the same iterator
              parentNodeId = sourceNode.parentId;
              if (direction === "top-to-bottom") {
                newPosition = {
                  x: sourceNode.position.x,
                  y: sourceNode.position.y + 150,
                };
              } else {
                newPosition = {
                  x: sourceNode.position.x + 250,
                  y: sourceNode.position.y,
                };
              }
            } else if (isContainerNodeType(sourceNode.type) && !forceInsideIterator) {
              // Source is iterator but not forcing inside - add outside to the right
              if (direction === "top-to-bottom") {
                newPosition = {
                  x: sourceNode.position.x,
                  y: sourceNode.position.y + 200,
                };
              } else {
                newPosition = {
                  x: sourceNode.position.x + 300,
                  y: sourceNode.position.y,
                };
              }
            } else {
              // Normal node - position beside it
              if (direction === "top-to-bottom") {
                newPosition = {
                  x: sourceNode.position.x,
                  y: sourceNode.position.y + 200,
                };
              } else {
                newPosition = {
                  x: sourceNode.position.x + 300,
                  y: sourceNode.position.y,
                };
              }
            }
          }
        } else if (nodes.length > 0) {
          // For additional nodes, position to avoid existing nodes
          if (direction === "top-to-bottom") {
            // Vertical layout
            const maxY = Math.max(...nodes.map((n) => n.position.y));
            newPosition = {
              x: 100,
              y: maxY + 250,
            };
          } else {
            // Horizontal layout
            const maxX = Math.max(...nodes.map((n) => n.position.x));
            newPosition = {
              x: maxX + 350,
              y: 100,
            };
          }
        } else {
          // First node - always start at a standard position
          newPosition = { x: 100, y: 100 };
        }

        // Generate stable node ID for React Flow and readable ID for backend
        const stableNodeId = `node-${nanoid()}`;
        const readableId =
          type === "trigger"
            ? get().generateTriggerNodeId("manual", null, nodes)
            : get().generateActionNodeId("Action", nodes);

        const newNode: CustomNode = {
          id: stableNodeId,
          type,
          position: newPosition,
          parentId: parentNodeId,
          // extent: parentNodeId ? ("parent" as const) : undefined,
          data: {
            readableId,
            input_schema: {},
            output_schema: {},
            label: `${type.charAt(0).toUpperCase() + type.slice(1)} Node`,
            type: type,
            triggerType: type === "trigger" ? "manual" : undefined,
            parentNode: parentNodeId,
            ...data,
          },
        };

        $rfInstance.addNodes(newNode);

        let newEdges = [...get().edges];
        if (sourceNodeId) {
          // Don't create edges between parent iterator and its first child
          // Children inside iterators connect to each other, not to parent
          const sourceNode = nodes.find((n) => n.id === sourceNodeId);
          const isFirstChildOfIterator =
            isContainerNodeType(sourceNode?.type) &&
            forceInsideIterator &&
            nodes.filter((n) => n.parentId === sourceNodeId).length === 0;

          if (!isFirstChildOfIterator) {
            const newEdge: Edge = {
              id: `${sourceNodeId}-${newNode.id}`.replace(/\./g, "_"),
              source: sourceNodeId,
              target: newNode.id,
              type: "animated",
              animated: true,
              sourceHandle: edgeColor,
              data: {},
            };
            $rfInstance.addEdges(newEdge);
            newEdges.push(newEdge);
          }
        }

        set({
          nodes: [...nodes, newNode],
          edges: newEdges,
          isDirty: true,
        });

        // Update iterator size if this node is a child of an iterator
        if (parentNodeId) {
          setTimeout(() => get().updateIteratorSize(parentNodeId), 100);
        }

        // Apply layout with the current direction
        requestAnimationFrame(() => {
          applyLayout("LR");
        });

        return newNode;
      },

      // Helper function to generate readable, unique IDs for trigger nodes
      generateTriggerNodeId: (triggerType: TriggerType, triggerConfig?: any, existingNodes?: CustomNode[]) => {
        let baseId: string;

        switch (triggerType) {
          case "manual":
            baseId = "manualTrigger";
            break;
          case "webhook":
            baseId = "webhookTrigger";
            break;
          case "interval":
            if (triggerConfig?.intervalValue && triggerConfig?.intervalUnit) {
              const value = triggerConfig.intervalValue;
              const unit = triggerConfig.intervalUnit;
              baseId = `every${value}${unit.charAt(0).toUpperCase() + unit.slice(1)}${value > 1 ? "s" : ""}`;
            } else {
              baseId = "intervalTrigger";
            }
            break;
          case "app":
            if (triggerConfig?.intervalValue && triggerConfig?.intervalUnit) {
              const value = triggerConfig.intervalValue;
              const unit = triggerConfig.intervalUnit;
              baseId = `every${value}${unit.charAt(0).toUpperCase() + unit.slice(1)}${value > 1 ? "s" : ""}`;
            } else {
              baseId = "pollingTrigger";
            }
            break;
          case "email":
            baseId = "emailTrigger";
            break;
          case "event":
            if (triggerConfig?.event?.record && triggerConfig?.event?.events?.length > 0) {
              const record = triggerConfig.event.record;
              const mainEvent = triggerConfig.event.events[0]; // Use the first event
              baseId = `${record}${mainEvent.charAt(0).toUpperCase() + mainEvent.slice(1)}`;
            } else {
              baseId = "eventTrigger";
            }
            break;
          default:
            baseId = "trigger";
        }

        // Replace dots with underscores to make it JavaScript object notation friendly
        baseId = baseId.replace(/\./g, "_");

        // If no existingNodes provided, return the baseId as-is
        if (!existingNodes) {
          return baseId;
        }

        // Check if this ID already exists and make it unique
        const existingIds = existingNodes.map((node) => node.data.readableId);
        let uniqueId = baseId;
        let counter = 1;

        while (existingIds.includes(uniqueId)) {
          uniqueId = `${baseId}${counter}`;
          counter++;
        }

        return uniqueId;
      },

      // Method to update nodes and edges from automation data
      updateNodesAndEdges: (automationNodes: any[], automationEdges: any[]) => {
        const { $rfInstance, setNodes, setEdges } = get();
        if (!$rfInstance) {
          throw new Error("ReactFlow instance not found");
        }

        console.log({ automationNodes, automationEdges });
        // Convert the nodes to the format expected by the store
        const nodes = automationNodes?.map((node: any) => {
          const nodeData: NodeData = {
            ...node.data,
            label: node.data.label,
            readableId: node.data.readableId, // Backend ID becomes readableId
            type: node.type as any, // Cast to any since we know the types are compatible
            event: node.data.event,
            triggerType: node.data.triggerType,
            recordTypes: node.data.recordTypes,
            intervalValue: node.data.intervalValue,
            intervalUnit: node.data.intervalUnit,
            allowedEmails: node.data.allowedEmails,
            triggerEmail: node.data.triggerEmail,
            webhookToken: node.data.webhookToken,
            webhookUrl: node.data.webhookUrl,
            authenticationHeader: node.data.authenticationHeader,
            app: node.data.app,
            position: node.position || { x: 0, y: 0 },
            parentNode: node.data.parentNode || node.parentNode,
            childNodes: node.data.childNodes,
            action: node.data.action
              ? {
                  id: node.data.action.id,
                  name: node.data.action.name,
                  description: node.data.action.description,
                  input_schema: node.data.action.input_schema || {},
                  output_schema: node.data.action.output_schema || {},
                  parameters: node.data.action.parameters ?? [],
                  logo_url: node.data.action?.logo_url,
                }
              : undefined,
            iterator: node.data.iterator,
            entityBatch: node.data.entityBatch ?? (node.data as any).recordBatch,
            input_schema: node.data.input_schema,
            output_schema: node.data.action?.output_schema || node.data.output_schema || {},
          };
          const convertedNode: CustomNode = {
            id: node.id,
            type: node.type,
            position: node.position || { x: 0, y: 0 },
            parentId: node.parentNode,
            // extent: node.parentNode ? ("parent" as const) : undefined,
            data: nodeData,
          };

          // Restore iterator dimensions
          if (isContainerNodeType(node.type) && (node.width || node.height)) {
            convertedNode.style = {
              width: node.width,
              height: node.height,
            };
          }

          return convertedNode;
        });

        const nodesList = nodes || [];

        // Validate readableId uniqueness
        const readableIdCounts = new Map<string, number>();
        nodesList.forEach((node) => {
          const rid = node.data.readableId;
          if (rid) {
            readableIdCounts.set(rid, (readableIdCounts.get(rid) || 0) + 1);
          }
        });

        const duplicates = Array.from(readableIdCounts.entries())
          .filter(([_, count]) => count > 1)
          .map(([rid]) => rid);

        if (duplicates.length > 0) {
          console.error(
            `[WorkflowBuilder] Duplicate readableIds detected: ${duplicates.join(
              ", ",
            )}. This may cause edge resolution issues.`,
          );
        }

        const readableIdMap = new Map<string, string>(
          nodesList
            .map((node) => [node.data.readableId, node.id] as [string, string])
            .filter(([rid, id]) => !!rid && !!id),
        );
        const idMap = new Map<string, string>(nodesList.map((node) => [node.id, node.id] as [string, string]));

        const resolveNodeId = (value: string | undefined): string | undefined => {
          if (!value) return undefined;
          return idMap.get(value) ?? readableIdMap.get(value);
        };

        const hasEdges = Array.isArray(automationEdges) && automationEdges.length > 0;
        if (hasEdges) {
          const validMappedEdges: Edge[] = [];
          const invalidEdges: any[] = [];

          automationEdges.forEach((edge: any) => {
            const resolvedSource = resolveNodeId(edge.source);
            const resolvedTarget = resolveNodeId(edge.target);

            if (resolvedSource && resolvedTarget) {
              validMappedEdges.push({
                ...edge,
                source: resolvedSource,
                target: resolvedTarget,
              });
            } else {
              invalidEdges.push({
                edge,
                resolvedSource,
                resolvedTarget,
                missing: !resolvedSource ? "source" : !resolvedTarget ? "target" : "both",
              });
            }
          });

          if (invalidEdges.length > 0) {
            console.error(
              `[WorkflowBuilder] Dropped ${invalidEdges.length} invalid edges due to unresolvable endpoints:`,
              invalidEdges,
            );
          }

          // Apply dagre layout directly with the loaded nodes and edges
          get().applyLayoutWithNodesAndEdges(nodesList, validMappedEdges, "LR");
        } else {
          // If no edges, just set the nodes with basic positioning
          $rfInstance.setNodes(nodesList);
          $rfInstance.setEdges([]);
          setNodes(nodesList);
          setEdges([]);
        }
      },

      // Method to apply layout using dagre
      applyLayout: (direction: "TB" | "LR" = "LR", forceLayout: boolean = false) => {
        const { $rfInstance } = get();
        if (!$rfInstance) {
          throw new Error("ReactFlow instance not found");
        }
        const nodes = $rfInstance?.getNodes() ?? [];
        const edges = $rfInstance?.getEdges() ?? [];
        if (nodes.length === 0) {
          return;
        }

        // Use the more specific method with current nodes and edges
        get().applyLayoutWithNodesAndEdges(nodes, edges, direction);
      },

      // Method to apply layout with specific nodes and edges
      applyLayoutWithNodesAndEdges: (inputNodes: CustomNode[], inputEdges: Edge[], direction: "TB" | "LR" = "LR") => {
        console.debug?.("🔧 applyLayoutWithNodesAndEdges", {
          nodeCount: inputNodes.length,
          edgeCount: inputEdges.length,
          direction,
        });
        const { nodes: currentNodes, $rfInstance, setNodes, setEdges } = get();
        if (!$rfInstance || inputNodes.length === 0) {
          return;
        }

        // Heuristic: If a single node is being added, position it manually. Otherwise, run a full layout.
        const isAddingSingleNode = inputNodes.length === currentNodes.length + 1;

        if (isAddingSingleNode) {
          const newNode = inputNodes.find((n) => !currentNodes.some((cn) => cn.id === n.id));
          if (!newNode) return; // Should not happen based on the length check

          const edge = inputEdges.find((e) => e.target === newNode.id);
          // If the new node has a parent, position it relative to the parent.
          if (edge) {
            const parentNode = currentNodes.find((n) => n.id === edge.source);

            if (parentNode?.position) {
              const parentNodeElement = document.querySelector(`[data-node-id="${parentNode.id}"]`);
              const parentWidth = parentNodeElement?.clientWidth || 250; // Fallback width
              const horizontalSpacing = 120; // Increased spacing to push it more to the right
              const verticalSpacing = 40; // Vertical gap between nodes

              // Default dimensions for the new node, as it hasn't been rendered yet.
              const newNodeWidth = 250;
              const newNodeHeight = 80;

              let newPosition = {
                x: parentNode.position.x + parentWidth + horizontalSpacing,
                y: parentNode.position.y,
              };

              // --- Overlap Prevention Logic ---
              let overlap;
              do {
                overlap = false;
                for (const existingNode of currentNodes) {
                  // Skip self-check
                  if (existingNode.id === newNode.id) continue;

                  const existingNodeWidth = existingNode.width || 250;
                  const existingNodeHeight = existingNode.height || 80;

                  // Check for bounding box collision
                  const boxesOverlap =
                    newPosition.x < existingNode.position.x + existingNodeWidth &&
                    newPosition.x + newNodeWidth > existingNode.position.x &&
                    newPosition.y < existingNode.position.y + existingNodeHeight &&
                    newPosition.y + newNodeHeight > existingNode.position.y;

                  if (boxesOverlap) {
                    // If overlap, move the new node down below the conflicting node
                    newPosition.y = existingNode.position.y + existingNodeHeight + verticalSpacing;
                    overlap = true;
                    break; // Restart the loop to check the new position against all nodes again
                  }
                }
              } while (overlap);

              // Mutate the new node's position directly
              newNode.position = newPosition;
            }
          }

          // Update store and React Flow instance with the correctly positioned new node
          $rfInstance.setNodes(inputNodes);
          $rfInstance.setEdges(inputEdges);
          setNodes(inputNodes);
          setEdges(inputEdges);

          setTimeout(() => {
            $rfInstance.fitView({ duration: 300, padding: 0.2 });
          }, 100);
        } else {
          $rfInstance.setNodes(inputNodes);
          $rfInstance.setEdges(inputEdges);
          setNodes(inputNodes);
          setEdges(inputEdges);
          setTimeout(() => {
            $rfInstance.fitView({ duration: 300, padding: 0.2 });
          }, 100);
        }
      },
    };
  });

export const useWorkflowBuilderStore = workflowBuilderStoreFn();
